/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {VIRTUAL_PRESET_ID} from '../../../constants/VIRTUAL_PRESET_ID';
import type {Preset, VirtualPreset} from '../../../types/Api';

/** Minimum drag distance (px) before a drag operation activates. */
export const DRAG_ACTIVATION_DISTANCE = 8;

/** Minimum height (px) of the dropdown-mode preset list — the pre-expansion cap. */
export const PRESET_LIST_MIN_HEIGHT_PX = 280;

/** Gap (px) kept between the dropdown-mode preset list and the bottom viewport edge. */
export const PRESET_LIST_BOTTOM_GAP_PX = 24;

/**
 * Sizes the dropdown-mode preset list to the space actually available below it:
 * viewport height minus the list's own measured top, minus a fixed bottom gap.
 * Used as a callback ref — the header stack above the trigger varies per app
 * shell, so a static `calc(100vh - N)` reserve either wastes space or overflows.
 *
 * The measurement is deferred by a double requestAnimationFrame: the ref fires
 * while the floating-ui portal still sits at (0,0), so an immediate
 * getBoundingClientRect() reads a bogus top and oversizes the list (verified in
 * Chrome — immediate measure gave top=47 for a menu that ends up at top=454).
 */
export function setPresetListMaxHeight(node: HTMLDivElement | null): void {
  if (!node) {
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!node.isConnected) {
        return;
      }
      const availableHeight =
        window.innerHeight - node.getBoundingClientRect().top - PRESET_LIST_BOTTOM_GAP_PX;
      node.style.maxHeight = `${Math.max(PRESET_LIST_MIN_HEIGHT_PX, availableHeight)}px`;
    });
  });
}

/**
 * Builds the synthetic, count-only Preset entry for the inactive virtual ("Default") tab.
 * Prepended to the visible list solely so usePresetRowCounts fetches its row count by
 * dataQueryId — it is never rendered as a real preset (hence the empty title/description).
 */
export function makeVirtualRowCountEntry(virtualPreset: VirtualPreset): Preset {
  return {
    id: VIRTUAL_PRESET_ID,
    title: '',
    description: '',
    dataQueryId: virtualPreset.dataQueryId,
    isActive: false,
  };
}
